import "./Invoices.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuthStore } from "../../../Services/authStore";
import {
  toastSuccess,
  toastError,
  toastWarning,
} from "../../../Services/toaster";
import editIcon from "../../../assets/icons/edit.png";
import deleteIcon from "../../../assets/icons/delete.png";
import BackIcon from "../../../assets/icons/arrow.png";

// ─────────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────────
const CURRENCY_OPTIONS = ["INR", "USD", "EUR", "GBP"];
const PAYMENT_TERMS = [
  "Net 15",
  "Net 30",
  "Net 45",
  "Net 60",
  "Due on Receipt",
];
const INVOICE_STATUS_OPTIONS = [
  "Draft",
  "Submitted",
  "Approved",
  "Paid",
  "Cancelled",
];

const EMPTY_LINE = {
  po_line_id: 0,
  line_no: 0,
  item_code: "",
  item_group: "",
  description: "",
  po_qty: 0,
  invoiced_qty: 0,
  remaining_qty: 0,
  unit: "",
  unit_price: 0,
  unit_price_fc: 0,
  line_total: 0,
  line_total_fc: 0,
  discount_percent: 0,
  discount_amount: 0,
  discount_amount_fc: 0,
  tax_code: "",
  tax_rate: 0,
  tax_amount: 0,
  tax_amount_fc: 0,
  total_amount: 0,
  total_amount_fc: 0,
  po_unit_price: 0,
  remarks: "",
};

const EMPTY_FORM = {
  po_id: "",
  po_number: "",
  invoice_number: "",
  vendor_code: "",
  vendor_name: "",
  vendor_invoice_number: "",
  invoice_date: "",
  due_date: "",
  currency: "INR",
  currency_rate: 1,
  payment_term: "Net 30",
  subtotal: 0,
  subtotal_fc: 0,
  vat_sum: 0,
  vat_sum_fc: 0,
  discount: 0,
  discount_fc: 0,
  invoice_total: 0,
  invoice_total_fc: 0,
  invoice_status: "Draft",
  remarks: "",
  lines: [],
};

// ─────────────────────────────────────────────────────────
//  INVOICES VIEW
// ─────────────────────────────────────────────────────────
function InvoicesView() {
  // ─── State ──────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // ─── Dropdown Data ──────────────────────────────────────
  const [vendors, setVendors] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── API Calls ──────────────────────────────────────────

  // GET /api/invoice/ - Get All Invoices
  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/invoice/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        params: { skip: 0, limit: 100 },
      });
      const data = response.data?.data || response.data || [];
      setInvoices(data);
    } catch (err) {
      console.error("Failed to load invoices:", err.response?.data);
      toastError("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  // GET /api/invoice/{invoice_id} - Get Invoice Details
  const fetchInvoiceById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/api/invoice/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });
        return response.data?.data || response.data;
      } catch (err) {
        console.error("Failed to fetch invoice:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── Fetch Dropdown Data ──────────────────────────────

  // GET /vendor/list/all
  const fetchVendors = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });
      const data = response.data?.data || response.data || [];
      setVendors(data.filter((v) => v.is_active === true || v.is_active === 1));
    } catch (err) {
      console.error("Failed to load vendors:", err);
    }
  }, [API_URL, token]);

  // GET /api/po/ - Get Purchase Orders with line items
  const fetchPurchaseOrders = useCallback(async () => {
    try {
      // First, get the list of all POs
      const response = await axios.get(`${API_URL}/api/po/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });
      const data = response.data?.data || response.data || [];
      console.log("Raw PO data:", data);

      // Filter only approved POs with valid po_number
      const approvedPOs = data.filter((po) => {
        const isApproved = po.approval_status === "Approved";
        const hasPoNumber = po.po_number && po.po_number !== "string";
        return isApproved && hasPoNumber;
      });

      console.log("Approved POs:", approvedPOs);

      // Fetch full details for each approved PO using /api/po/{po_id}
      const fullPOs = await Promise.all(
        approvedPOs.map(async (po) => {
          try {
            // Use the GET /api/po/{po_id} endpoint
            const detailResponse = await axios.get(
              `${API_URL}/api/po/${po.id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  accept: "application/json",
                },
              },
            );

            const poDetail = detailResponse.data?.data || detailResponse.data;
            console.log(`PO ${po.id} details fetched:`, poDetail);

            // Ensure we have the lines data
            if (poDetail.lines && poDetail.lines.length > 0) {
              console.log(
                `PO ${po.id} has ${poDetail.lines.length} line items`,
              );
            } else {
              console.log(`PO ${po.id} has no line items`);
            }

            return poDetail;
          } catch (err) {
            console.error(`Failed to fetch PO ${po.id} details:`, err);
            // Return the basic PO data if detail fetch fails
            return po;
          }
        }),
      );

      console.log("Full PO data with lines:", fullPOs);
      setPurchaseOrders(fullPOs);
    } catch (err) {
      console.error("Failed to load purchase orders:", err);
      toastError("Failed to load purchase orders");
    }
  }, [API_URL, token]);

  const fetchDropdownData = useCallback(async () => {
    if (dropdownsLoaded) return;

    setLoadingDropdowns(true);
    try {
      await Promise.all([fetchVendors(), fetchPurchaseOrders()]);
      setDropdownsLoaded(true);
    } catch (err) {
      console.error("Failed to load dropdown data:", err);
      toastError("Failed to load form data");
    } finally {
      setLoadingDropdowns(false);
    }
  }, [fetchVendors, fetchPurchaseOrders, dropdownsLoaded]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // ─── CRUD Operations ────────────────────────────────────

  // POST /api/invoice/create - Create Invoice
  const createInvoice = async (invoiceData) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/invoice/create`,
        invoiceData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Create invoice error:", err.response?.data);
      throw err;
    }
  };

  // PUT /api/invoice/{invoice_id}/update - Update Invoice
  const updateInvoice = async (id, invoiceData) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/invoice/${id}/update`,
        invoiceData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update invoice error:", err.response?.data);
      throw err;
    }
  };

  // DELETE /api/invoice/{invoice_id}/delete - Delete Invoice
  const deleteInvoice = async (id) => {
    try {
      const response = await axios.delete(
        `${API_URL}/api/invoice/${id}/delete`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Delete invoice error:", err.response?.data);
      throw err;
    }
  };

  // ─── Form Handlers ──────────────────────────────────────

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openAdd = async () => {
    setIsEditMode(false);
    setEditingId(null);
    setFormError("");
    setShowForm(true);
    setFormLoading(true);

    try {
      await fetchDropdownData();

      setForm({
        ...EMPTY_FORM,
        invoice_date: new Date().toISOString().split("T")[0],
        invoice_number: "",
      });
    } catch (err) {
      setForm({
        ...EMPTY_FORM,
        invoice_date: new Date().toISOString().split("T")[0],
        invoice_number: "",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const openEdit = async (invoice) => {
    await fetchDropdownData();

    setFormError("");
    setIsEditMode(true);
    setEditingId(invoice.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const data = await fetchInvoiceById(invoice.id);
      setForm({
        po_id: data.po_id || "",
        po_number: data.po_number || "",
        invoice_number: data.invoice_number || "",
        vendor_code: data.vendor_code || "",
        vendor_name: data.vendor_name || "",
        vendor_invoice_number: data.vendor_invoice_number || "",
        invoice_date: data.invoice_date || "",
        due_date: data.due_date || "",
        currency: data.currency || "INR",
        currency_rate: data.currency_rate || 1,
        payment_term: data.payment_term || "Net 30",
        subtotal: data.subtotal || 0,
        subtotal_fc: data.subtotal_fc || 0,
        vat_sum: data.vat_sum || 0,
        vat_sum_fc: data.vat_sum_fc || 0,
        discount: data.discount || 0,
        discount_fc: data.discount_fc || 0,
        invoice_total: data.invoice_total || 0,
        invoice_total_fc: data.invoice_total_fc || 0,
        invoice_status: data.invoice_status || "Draft",
        remarks: data.remarks || "",
        lines: data.lines || [],
      });
    } catch (err) {
      setFormError("Failed to load invoice details.");
      toastError("Failed to load invoice details.");
    } finally {
      setFormLoading(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setForm({ ...EMPTY_FORM });
    setFormError("");
    setIsEditMode(false);
    setEditingId(null);
  };

  // ─── Line Item Handlers ─────────────────────────────────

  const addLine = () => {
    const newLine = {
      ...EMPTY_LINE,
      line_no: form.lines.length + 1,
    };
    setF("lines", [...form.lines, newLine]);
  };

  const updateLine = (index, field, value) => {
    const updatedLines = [...form.lines];
    const line = { ...updatedLines[index] };
    line[field] = value;

    // Auto-calculate totals
    const qty = parseFloat(line.invoiced_qty) || 0;
    const price = parseFloat(line.unit_price) || 0;
    const discountPercent = parseFloat(line.discount_percent) || 0;
    const taxRate = parseFloat(line.tax_rate) || 0;

    line.line_total = qty * price;
    line.discount_amount = (line.line_total * discountPercent) / 100;
    line.line_total_fc = line.line_total;
    line.discount_amount_fc = line.discount_amount;

    const afterDiscount = line.line_total - line.discount_amount;
    line.tax_amount = (afterDiscount * taxRate) / 100;
    line.tax_amount_fc = line.tax_amount;
    line.total_amount = afterDiscount + line.tax_amount;
    line.total_amount_fc = line.total_amount;

    updatedLines[index] = line;
    setF("lines", updatedLines);
    calculateTotals(updatedLines);
  };

  const removeLine = (index) => {
    const updatedLines = form.lines.filter((_, i) => i !== index);
    updatedLines.forEach((line, i) => (line.line_no = i + 1));
    setF("lines", updatedLines);
    calculateTotals(updatedLines);
  };

  const calculateTotals = (lines) => {
    let subtotal = 0;
    let vatSum = 0;
    let discount = 0;
    let invoiceTotal = 0;

    lines.forEach((line) => {
      const lineTotal = parseFloat(line.line_total) || 0;
      const discountAmount = parseFloat(line.discount_amount) || 0;
      const taxAmount = parseFloat(line.tax_amount) || 0;
      const totalAmount = parseFloat(line.total_amount) || 0;

      subtotal += lineTotal;
      discount += discountAmount;
      vatSum += taxAmount;
      invoiceTotal += totalAmount;
    });

    setF("subtotal", subtotal);
    setF("subtotal_fc", subtotal);
    setF("discount", discount);
    setF("discount_fc", discount);
    setF("vat_sum", vatSum);
    setF("vat_sum_fc", vatSum);
    setF("invoice_total", invoiceTotal);
    setF("invoice_total_fc", invoiceTotal);
  };

  // ─── Submit Handlers ────────────────────────────────────

  const handleSubmit = async (status = "Draft") => {
    setFormError("");

    if (!form.po_id) {
      setFormError("PO Number is required.");
      toastWarning("PO Number is required.");
      return;
    }
    if (!form.vendor_code) {
      setFormError("Vendor is required.");
      toastWarning("Vendor is required.");
      return;
    }
    if (!form.invoice_date) {
      setFormError("Invoice date is required.");
      toastWarning("Invoice date is required.");
      return;
    }
    if (form.lines.length === 0) {
      setFormError("At least one line item is required.");
      toastWarning("At least one line item is required.");
      return;
    }

    // Validate lines
    for (let i = 0; i < form.lines.length; i++) {
      const line = form.lines[i];
      if (!line.description.trim()) {
        setFormError(`Line ${i + 1}: Description is required.`);
        toastWarning(`Line ${i + 1}: Description is required.`);
        return;
      }
      const invoicedQty = parseFloat(line.invoiced_qty);
      if (isNaN(invoicedQty) || invoicedQty <= 0) {
        setFormError(`Line ${i + 1}: Valid invoiced quantity is required.`);
        toastWarning(`Line ${i + 1}: Valid invoiced quantity is required.`);
        return;
      }
      const remainingQty = parseFloat(line.remaining_qty) || 0;
      if (remainingQty > 0 && invoicedQty > remainingQty) {
        setFormError(
          `Line ${i + 1}: Cannot invoice more than remaining quantity (${remainingQty}).`,
        );
        toastWarning(
          `Line ${i + 1}: Cannot invoice more than remaining quantity (${remainingQty}).`,
        );
        return;
      }
      if (!line.unit_price || parseFloat(line.unit_price) <= 0) {
        setFormError(`Line ${i + 1}: Valid unit price is required.`);
        toastWarning(`Line ${i + 1}: Valid unit price is required.`);
        return;
      }
    }

    const payload = {
      ...form,
      invoice_status: status,
      invoice_number: form.invoice_number || "",
    };

    // Ensure correct types
    payload.po_id = parseInt(payload.po_id) || 0;
    payload.currency_rate = parseFloat(payload.currency_rate) || 1;
    payload.subtotal = parseFloat(payload.subtotal) || 0;
    payload.subtotal_fc = parseFloat(payload.subtotal_fc) || 0;
    payload.vat_sum = parseFloat(payload.vat_sum) || 0;
    payload.vat_sum_fc = parseFloat(payload.vat_sum_fc) || 0;
    payload.discount = parseFloat(payload.discount) || 0;
    payload.discount_fc = parseFloat(payload.discount_fc) || 0;
    payload.invoice_total = parseFloat(payload.invoice_total) || 0;
    payload.invoice_total_fc = parseFloat(payload.invoice_total_fc) || 0;

    payload.lines = payload.lines.map((line) => ({
      ...line,
      po_qty: parseFloat(line.po_qty) || 0,
      invoiced_qty: parseFloat(line.invoiced_qty) || 0,
      remaining_qty: parseFloat(line.remaining_qty) || 0,
      unit_price: parseFloat(line.unit_price) || 0,
      unit_price_fc: parseFloat(line.unit_price) || 0,
      line_total: parseFloat(line.line_total) || 0,
      line_total_fc: parseFloat(line.line_total) || 0,
      discount_percent: parseFloat(line.discount_percent) || 0,
      discount_amount: parseFloat(line.discount_amount) || 0,
      discount_amount_fc: parseFloat(line.discount_amount) || 0,
      tax_rate: parseFloat(line.tax_rate) || 0,
      tax_amount: parseFloat(line.tax_amount) || 0,
      tax_amount_fc: parseFloat(line.tax_amount) || 0,
      total_amount: parseFloat(line.total_amount) || 0,
      total_amount_fc: parseFloat(line.total_amount) || 0,
    }));

    setFormLoading(true);

    try {
      if (isEditMode) {
        await updateInvoice(editingId, payload);
        toastSuccess("Invoice updated successfully.");
      } else {
        const response = await createInvoice(payload);
        toastSuccess(
          `Invoice created: ${response.invoice_number || "Successfully"}`,
        );
      }
      closeForm();
      fetchInvoices();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save invoice.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) {
      return;
    }

    try {
      await deleteInvoice(id);
      toastSuccess("Invoice deleted successfully.");
      fetchInvoices();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toastError(detail || "Failed to delete invoice.");
    }
  };

  // ── Filters ─────────────────────────────────────────────

  const filteredInvoices = invoices.filter((invoice) => {
    const matchStatus =
      statusFilter === "All" || invoice.invoice_status === statusFilter;
    const matchSearch =
      (invoice.invoice_number || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (invoice.vendor_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (invoice.vendor_code || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (invoice.po_number || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  // ── Format Helpers ──────────────────────────────────────

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusClass = (status) => {
    const map = {
      Draft: "inv-status-draft",
      Submitted: "inv-status-submitted",
      Approved: "inv-status-approved",
      Paid: "inv-status-paid",
      Cancelled: "inv-status-cancelled",
    };
    return map[status] || "inv-status-draft";
  };

  // ─── Handle PO Selection ─────────────────────────────────

  const handlePOSelection = (selectedValue) => {
    console.log("Selected PO ID:", selectedValue);

    if (!selectedValue) {
      setF("po_id", "");
      setF("po_number", "");
      setF("vendor_code", "");
      setF("vendor_name", "");
      setF("lines", []);
      calculateTotals([]);
      return;
    }

    const po = purchaseOrders.find((p) => p.id === parseInt(selectedValue));
    console.log("Selected PO full data:", po);

    if (po) {
      // Set basic PO info
      setF("po_id", selectedValue);
      setF("po_number", po.po_number || "");
      setF("vendor_code", po.vendor_code || "");
      setF("vendor_name", po.vendor_name || "");

      // Check if PO has lines
      if (po.lines && po.lines.length > 0) {
        console.log(`Loading ${po.lines.length} line items from PO`);
        console.log("PO Lines data:", po.lines);

        // Map PO lines to invoice lines
        const lines = po.lines
          .map((line, index) => {
            // Calculate remaining quantity
            const consumedQty = line.consumed_qty || 0;
            const poQty = line.qty || 0;
            const remainingQty = poQty - consumedQty;

            console.log(
              `Line ${index + 1}: Description=${line.description}, PO Qty=${poQty}, Consumed=${consumedQty}, Remaining=${remainingQty}`,
            );

            // Skip fully consumed lines (remaining quantity <= 0)
            if (remainingQty <= 0) {
              console.log(`Line ${index + 1} is fully consumed, skipping`);
              return null;
            }

            // Calculate line totals
            const unitPrice = line.price || 0;
            const discountPercent = line.discount_percent || 0;
            const taxRate = line.tax_rate || 0;

            const lineTotal = remainingQty * unitPrice;
            const discountAmount = (lineTotal * discountPercent) / 100;
            const afterDiscount = lineTotal - discountAmount;
            const taxAmount = (afterDiscount * taxRate) / 100;
            const totalAmount = afterDiscount + taxAmount;

            return {
              po_line_id: line.id || 0,
              line_no: index + 1,
              item_code: line.item_code || "",
              item_group: line.item_group || "",
              description: line.description || "",
              po_qty: poQty,
              invoiced_qty: remainingQty, // Set to remaining qty by default
              remaining_qty: remainingQty,
              unit: line.unit || "",
              unit_price: unitPrice,
              unit_price_fc: unitPrice,
              line_total: lineTotal,
              line_total_fc: lineTotal,
              discount_percent: discountPercent,
              discount_amount: discountAmount,
              discount_amount_fc: discountAmount,
              tax_code: line.tax_code || "",
              tax_rate: taxRate,
              tax_amount: taxAmount,
              tax_amount_fc: taxAmount,
              total_amount: totalAmount,
              total_amount_fc: totalAmount,
              po_unit_price: unitPrice,
              remarks: line.remarks || "",
            };
          })
          .filter((line) => line !== null); // Remove null entries (fully consumed lines)

        console.log(`Mapped ${lines.length} invoice lines from PO`);
        console.log("Invoice lines:", lines);

        if (lines.length > 0) {
          setF("lines", lines);
          calculateTotals(lines);
          toastSuccess(
            `Loaded ${lines.length} line items from PO ${po.po_number}`,
          );
        } else {
          toastWarning("All line items in this PO are fully consumed");
          setF("lines", []);
          calculateTotals([]);
        }
      } else {
        console.log("No lines found in PO");
        setF("lines", []);
        calculateTotals([]);
        toastWarning("No line items found in this PO");
      }
    } else {
      // Reset if no PO found
      toastError("PO not found");
      setF("po_id", "");
      setF("po_number", "");
      setF("vendor_code", "");
      setF("vendor_name", "");
      setF("lines", []);
      calculateTotals([]);
    }
  };

  // ─── Form View ──────────────────────────────────────────

  if (showForm) {
    return (
      <div className="inv-form-page">
        <div className="inv-form-header">
          <div className="inv-form-header-left">
            <button className="inv-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="inv-back-icon" />
            </button>
            <div>
              <h2 className="inv-form-title">
                {isEditMode ? "Edit Invoice" : "New Invoice"}
              </h2>
              <p className="inv-form-subtitle">
                {isEditMode ? "Update invoice details" : "Create a new invoice"}
              </p>
            </div>
          </div>
        </div>

        <div className="inv-form-card">
          {loadingDropdowns && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              Loading form data...
            </p>
          )}
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && <p className="inv-form-error">{formError}</p>}

          <div className="inv-form-section">
            <h3>Invoice Information</h3>
            <div className="inv-form-grid">
              <div className="inv-form-group">
                <label>Invoice Number</label>
                <input
                  value={
                    isEditMode ? form.invoice_number || "N/A" : "Auto-generated"
                  }
                  disabled
                  style={{
                    background: "#f5f5f5",
                    fontWeight: isEditMode ? "600" : "400",
                    color: isEditMode ? "#2A070C" : "#A27B5B",
                  }}
                />
                {!isEditMode && (
                  <div className="inv-sequence-info">
                    <span className="inv-sequence-label">
                      Invoice number will be auto-generated
                    </span>
                  </div>
                )}
              </div>
              <div className="inv-form-group">
                <label>PO Number *</label>
                <select
                  name="po_id"
                  value={form.po_id}
                  onChange={(e) => handlePOSelection(e.target.value)}
                  disabled={loadingDropdowns || isEditMode}
                >
                  <option value="">Select PO</option>
                  {purchaseOrders.length === 0 ? (
                    <option value="" disabled>
                      No approved POs available
                    </option>
                  ) : (
                    purchaseOrders.map((po) => (
                      <option key={po.id} value={po.id}>
                        {po.po_number} - {po.vendor_name}
                        {po.doc_total
                          ? ` (${formatCurrency(po.doc_total)})`
                          : ""}
                      </option>
                    ))
                  )}
                </select>
                {purchaseOrders.length === 0 && !loadingDropdowns && (
                  <div
                    className="inv-form-hint"
                    style={{
                      color: "#B7950B",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    No approved POs available. Please ensure POs are approved
                    first.
                  </div>
                )}
              </div>
              <div className="inv-form-group">
                <label>Vendor Code</label>
                <input
                  value={form.vendor_code}
                  disabled
                  style={{ background: "#f5f5f5" }}
                />
              </div>
              <div className="inv-form-group">
                <label>Vendor Name</label>
                <input
                  value={form.vendor_name}
                  disabled
                  style={{ background: "#f5f5f5" }}
                />
              </div>
              <div className="inv-form-group">
                <label>Invoice Date *</label>
                <input
                  name="invoice_date"
                  type="date"
                  value={form.invoice_date}
                  onChange={(e) => setF("invoice_date", e.target.value)}
                />
              </div>
              <div className="inv-form-group">
                <label>Due Date</label>
                <input
                  name="due_date"
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setF("due_date", e.target.value)}
                />
              </div>
              <div className="inv-form-group">
                <label>Vendor Invoice Number</label>
                <input
                  name="vendor_invoice_number"
                  value={form.vendor_invoice_number}
                  onChange={(e) =>
                    setF("vendor_invoice_number", e.target.value)
                  }
                  placeholder="Vendor's invoice number"
                />
              </div>
              <div className="inv-form-group">
                <label>Currency</label>
                <select
                  name="currency"
                  value={form.currency}
                  onChange={(e) => setF("currency", e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="inv-form-group">
                <label>Currency Rate</label>
                <input
                  name="currency_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.currency_rate}
                  onChange={(e) => setF("currency_rate", e.target.value)}
                />
              </div>
              <div className="inv-form-group">
                <label>Payment Terms</label>
                <select
                  name="payment_term"
                  value={form.payment_term}
                  onChange={(e) => setF("payment_term", e.target.value)}
                >
                  {PAYMENT_TERMS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div className="inv-form-group">
                <label>Status</label>
                <select
                  name="invoice_status"
                  value={form.invoice_status}
                  onChange={(e) => setF("invoice_status", e.target.value)}
                  disabled={!isEditMode}
                >
                  {INVOICE_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="inv-form-section">
            <div className="inv-section-header">
              <h3>Line Items</h3>
              <button
                className="inv-btn inv-btn-primary inv-btn-sm"
                onClick={addLine}
                disabled={loadingDropdowns || !form.po_id}
              >
                + Add Line
              </button>
            </div>

            <div className="inv-lines-wrap">
              <table className="inv-lines-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th>PO Qty</th>
                    <th>Invoiced Qty</th>
                    <th>Remaining Qty</th>
                    <th>Unit</th>
                    <th>Unit Price</th>
                    <th>Discount %</th>
                    <th>Tax %</th>
                    <th>Total</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="inv-lines-empty">
                        {!form.po_id
                          ? "Please select a PO first to load line items."
                          : "No items available. Click 'Add Line' to start."}
                      </td>
                    </tr>
                  ) : (
                    form.lines.map((line, index) => (
                      <tr key={index}>
                        {/* Sr. No. */}
                        <td className="inv-sr-no">{index + 1}</td>

                        {/* Description */}
                        <td>
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) =>
                              updateLine(index, "description", e.target.value)
                            }
                            placeholder="Description"
                            disabled={loadingDropdowns}
                          />
                        </td>

                        {/* PO Quantity */}
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={line.po_qty}
                            disabled
                            style={{ background: "#f5f5f5", width: "60px" }}
                          />
                        </td>

                        {/* Invoiced Quantity */}
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={line.invoiced_qty}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const maxQty = line.remaining_qty || 0;

                              if (maxQty > 0 && val > maxQty) {
                                toastWarning(
                                  `Cannot invoice more than remaining quantity (${maxQty})`,
                                );
                                return;
                              }

                              updateLine(index, "invoiced_qty", val);
                            }}
                            style={{ width: "60px" }}
                            disabled={loadingDropdowns}
                          />
                        </td>

                        {/* Remaining Quantity */}
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={line.remaining_qty}
                            disabled
                            style={{ background: "#f5f5f5", width: "60px" }}
                          />
                        </td>

                        {/* Unit */}
                        <td>
                          <input
                            type="text"
                            value={line.unit}
                            onChange={(e) =>
                              updateLine(index, "unit", e.target.value)
                            }
                            placeholder="Unit"
                            disabled={loadingDropdowns}
                            style={{ width: "70px" }}
                          />
                        </td>

                        {/* Unit Price */}
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.unit_price}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const maxPrice = line.po_unit_price || 0;

                              if (maxPrice > 0 && val > maxPrice) {
                                toastWarning(
                                  `Unit price cannot exceed PO price (${formatCurrency(maxPrice)})`,
                                );
                                return;
                              }

                              updateLine(index, "unit_price", val);
                            }}
                            style={{ width: "80px" }}
                            disabled={loadingDropdowns}
                          />
                        </td>

                        {/* Discount */}
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={line.discount_percent}
                            onChange={(e) =>
                              updateLine(
                                index,
                                "discount_percent",
                                e.target.value,
                              )
                            }
                            style={{ width: "60px" }}
                            disabled={loadingDropdowns}
                          />
                        </td>

                        {/* Tax Rate */}
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={line.tax_rate}
                            onChange={(e) =>
                              updateLine(index, "tax_rate", e.target.value)
                            }
                            style={{ width: "60px" }}
                            disabled={loadingDropdowns}
                          />
                        </td>

                        {/* Total Amount */}
                        <td>{formatCurrency(line.total_amount)}</td>

                        {/* Action */}
                        <td>
                          <button
                            className="inv-action-btn inv-action-btn-delete"
                            onClick={() => removeLine(index)}
                            title="Remove"
                            disabled={loadingDropdowns}
                          >
                            <img
                              src={deleteIcon}
                              alt="Delete"
                              className="inv-action-icon"
                            />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="inv-form-grid">
            <div className="inv-form-group inv-col-full">
              <label>Remarks</label>
              <textarea
                rows="3"
                value={form.remarks}
                onChange={(e) => setF("remarks", e.target.value)}
                placeholder="Additional remarks..."
                disabled={loadingDropdowns}
              />
            </div>
          </div>

          {form.lines.length > 0 && (
            <div className="inv-totals">
              <div className="inv-total-item">
                <span>Subtotal</span>
                <strong>{formatCurrency(form.subtotal)}</strong>
              </div>
              <div className="inv-total-item">
                <span>Discount</span>
                <strong>{formatCurrency(form.discount)}</strong>
              </div>
              <div className="inv-total-item">
                <span>Tax</span>
                <strong>{formatCurrency(form.vat_sum)}</strong>
              </div>
              <div className="inv-total-item inv-total-grand">
                <span>Invoice Total</span>
                <strong>{formatCurrency(form.invoice_total)}</strong>
              </div>
            </div>
          )}

          <div className="inv-form-footer">
            <button className="inv-btn inv-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            {isEditMode && (
              <>
                <button
                  className="inv-btn inv-btn-draft"
                  onClick={() => handleSubmit("Draft")}
                  disabled={formLoading || loadingDropdowns}
                >
                  Save Draft
                </button>
                <button
                  className="inv-btn inv-btn-primary"
                  onClick={() => handleSubmit("Submitted")}
                  disabled={formLoading || loadingDropdowns}
                >
                  {formLoading ? "Saving..." : "Submit"}
                </button>
              </>
            )}
            {!isEditMode && (
              <>
                <button
                  className="inv-btn inv-btn-draft"
                  onClick={() => handleSubmit("Draft")}
                  disabled={formLoading || loadingDropdowns}
                >
                  Save Draft
                </button>
                <button
                  className="inv-btn inv-btn-primary"
                  onClick={() => handleSubmit("Submitted")}
                  disabled={formLoading || loadingDropdowns}
                >
                  {formLoading ? "Creating..." : "Submit"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ──────────────────────────────────────────

  return (
    <div className="inv-list-view">
      <div className="inv-page-header">
        <div className="inv-page-header-left">
          <h1 className="inv-page-title">Invoices</h1>
          <p className="inv-page-subtitle">Manage invoices and billing</p>
        </div>
        <div className="inv-page-header-right">
          <div className="inv-search-box">
            <span className="inv-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="inv-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Paid">Paid</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <button className="inv-btn inv-btn-primary" onClick={openAdd}>
            + Create Invoice
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="inv-table-card">
        <div className="inv-table-responsive">
          <table className="inv-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Invoice Number</th>
                <th>PO Number</th>
                <th>Vendor</th>
                <th>Date</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="inv-empty-row">
                    Loading...
                  </td>
                </tr>
              ) : filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="inv-empty-state">
                      <div className="inv-empty-icon">📄</div>
                      <div className="inv-empty-title">No Invoices Found</div>
                      <div className="inv-empty-desc">
                        {searchTerm
                          ? `No results for "${searchTerm}"`
                          : "Create your first invoice"}
                      </div>
                      {!searchTerm && (
                        <button
                          className="inv-btn inv-btn-primary"
                          onClick={openAdd}
                        >
                          + Create Invoice
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice, index) => (
                  <tr key={invoice.id}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="inv-number-badge">
                        {invoice.invoice_number}
                      </span>
                    </td>
                    <td>
                      <span className="inv-po-number">
                        {invoice.po_number || "-"}
                      </span>
                    </td>
                    <td>
                      <div className="inv-vendor-info">
                        <div className="inv-vendor-code">
                          {invoice.vendor_code}
                        </div>
                        <div className="inv-vendor-name">
                          {invoice.vendor_name}
                        </div>
                      </div>
                    </td>
                    <td>{formatDate(invoice.invoice_date)}</td>
                    <td>{formatCurrency(invoice.invoice_total)}</td>
                    <td>
                      <span
                        className={`inv-status-badge ${getStatusClass(invoice.invoice_status)}`}
                      >
                        {invoice.invoice_status || "Draft"}
                      </span>
                    </td>
                    <td>
                      <div className="inv-action-group">
                        <button
                          className="inv-action-btn inv-action-btn-edit"
                          onClick={() => openEdit(invoice)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="inv-action-icon"
                          />
                        </button>
                        {(invoice.invoice_status === "Draft" ||
                          invoice.invoice_status === "Submitted") && (
                          <button
                            className="inv-action-btn inv-action-btn-delete"
                            onClick={() => handleDelete(invoice.id)}
                            title="Delete"
                          >
                            <img
                              src={deleteIcon}
                              alt="Delete"
                              className="inv-action-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  ROOT — Invoices Page
// ─────────────────────────────────────────────────────────
export default function Invoices() {
  return (
    <div className="inv-wrapper">
      <InvoicesView />
    </div>
  );
}
